"use client";

import axios from "axios";
import { useState, type ChangeEvent, type FormEvent } from "react";
import { logEvent } from "@/ui/analytics/google";

const API_URL = process.env.NEXT_PUBLIC_MICROGRAPHER_API_URL;

const API_KEY = process.env.NEXT_PUBLIC_MICROGRAPHER_API_KEY;

interface RegistryData {
	registry_id: string;
	artist: string;
	title: string;
	description: string;
	edition_num: string;
	edition_size: string;
	media_type: string;
	date_issued: string;
	issued_by: string;
}

const RegistryPage = () => {
	const [registryId, setRegistryId] = useState("");
	const [registryData, setRegistryData] = useState<RegistryData | null>(null);
	const [error, setError] = useState("");

	const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		setRegistryId(e.target.value);
	};

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setError("");
		try {
			// Log click event to GA
			logEvent("Click", "Registry Search", registryId);

			const response = await axios.get<RegistryData>(`${API_URL}/registry/${registryId}`, {
				withCredentials: true,
				headers: {
					"x-api-key": API_KEY,
				},
			});
			if (!response.data) {
				setError("Error fetching registry data. Please check the registry ID and try again.");
			}
			setRegistryData(response.data);
		} catch (err) {
			setRegistryData(null);
		}
	};
	const handleBack = () => {
		setRegistryData(null);
		setRegistryId("");
	};

	return (
		<section className="mx-auto max-w-gallery px-5 py-section-sm sm:px-8">
			<header className="mx-auto max-w-prose text-center">
				<p className="label-caps mb-4 text-ink-muted">Certificate of Authenticity</p>
				<h1 className="font-serif text-display font-light text-mg-black">The Registry</h1>
				<p className="mt-6 leading-relaxed text-ink-muted">
					Every limited edition is entered into the Micrographer registry and issued a signed Certificate of
					Authenticity. Enter a Registry ID to confirm a work&apos;s provenance.
				</p>
			</header>

			{!registryData ? (
				<form onSubmit={handleSubmit} className="mx-auto mt-12 max-w-md">
					<label htmlFor="registryId" className="label-caps mb-3 block text-ink-muted">
						Registry ID
					</label>
					<div className="flex border-b border-mg-black/20 focus-within:border-mg-gold-deep">
						<input
							id="registryId"
							name="registryId"
							type="text"
							value={registryId}
							onChange={handleInputChange}
							required
							placeholder="Found on your Certificate of Authenticity"
							className="w-full border-0 bg-transparent px-0 py-2 text-mg-black placeholder:text-ink-faint focus:ring-0"
						/>
					</div>
					{error ? <p className="mt-4 text-sm text-ink-muted">{error}</p> : null}
					<button
						type="submit"
						className="label-caps mt-8 w-full bg-mg-black px-7 py-4 text-bone transition-colors duration-300 ease-gallery hover:bg-mg-gold hover:text-mg-black"
					>
						Authenticate
					</button>
				</form>
			) : (
				<div className="mx-auto mt-12 max-w-2xl">
					{/* The archival record — a bordered accession document with a gold seal. */}
					<article className="border-mg-black/15 relative border bg-bone px-8 py-12 sm:px-14">
						{/* Emblem / seal */}
						<div className="mb-10 text-center">
							<div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-mg-gold-deep/60 font-serif text-xl text-mg-gold-deep">
								M
							</div>
							<p className="label-caps mt-5 text-ink-muted">Certificate of Authenticity</p>
							<p className="mt-2 font-serif text-2xl font-light text-mg-black">Verified &amp; registered</p>
						</div>

						<div className="hairline-gold mx-auto mb-10 w-16" />

						{/* Title + edition prominently */}
						<div className="mb-10 text-center">
							<h2 className="font-serif text-title font-light text-mg-black">{registryData.title}</h2>
							{registryData.description ? (
								<p className="mx-auto mt-3 max-w-prose text-sm leading-relaxed text-ink-muted">
									{registryData.description}
								</p>
							) : null}
							<p className="label-caps mt-5 text-mg-gold-deep">
								Edition {registryData.edition_num} of {registryData.edition_size ?? "—"}
							</p>
						</div>

						{/* Accession fields */}
						<dl className="space-y-4 border-t border-mg-black/10 pt-8">
							<AccRow label="Registry ID">{registryData.registry_id}</AccRow>
							<AccRow label="Artist">{registryData.artist}</AccRow>
							<AccRow label="Medium">{registryData.media_type}</AccRow>
							<AccRow label="Date issued">{registryData.date_issued}</AccRow>
							<AccRow label="Issued by">{registryData.issued_by}</AccRow>
						</dl>
					</article>

					<button onClick={handleBack} className="link-gallery mt-8 text-sm">
						Authenticate another work
					</button>
				</div>
			)}
		</section>
	);
};

// A quiet accession-record field row.
function AccRow({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div className="flex gap-6">
			<dt className="label-caps w-32 shrink-0 pt-0.5 text-ink-faint">{label}</dt>
			<dd className="text-sm text-mg-black">{children}</dd>
		</div>
	);
}

export default RegistryPage;
